// The OUTLINE — the map lab's third variable (`/_/map-lab`).
//
// The bivariate fill carries two variables and has room for nothing else: a
// third variable drawn as another fill is a fourth colour in a cell that already
// has one. The operator's idea, approved: carry the third as a BOLD OUTLINE.
// That is also the only way a SPARSE variable can be shown at all — a value true
// in a handful of cells is invisible as a fill (it becomes one more cell among
// thousands) and unmistakable as a contour around the places it is true.
//
// Two things make this a third variable rather than a highlight:
//
//   * the OUTLINE'S OWN VARIABLE. It may read the fill's own tile (the combined
//     tile's per-cell counts) or ANOTHER H3 layer's per-cell field — the
//     coverage tile's `n_treated`, the status tile's `n_production`/`n_needs` —
//     joined by `cell` id. Every H3 layer shares the tessellation AND the tiling
//     scheme, so the cell at a z/x/y is the same cell in both tiles; the join is
//     an id match, never a geometry operation (see `sharedCells`, and the
//     report's count of cells that resolve on both sides).
//
//   * the DISSOLVE. In cluster mode the outline is not one ring per cell, it is
//     the boundary of the UNION of the class's cells, so contiguous cells become
//     ONE contour and the picture is blobs rather than a mosaic. That union
//     happens on the tile server (`ST_Boundary(ST_Union(hexagons WHERE class))`,
//     see the `service-area-h3-outline-*` layers in etc/tegola.toml) and is the
//     reason it is seamless: the client could only ever cancel the interior
//     edges it can see, i.e. inside one tile, and would leave a hairline down
//     every tile boundary a blob crossed. Individual-cell mode is the trivial
//     case: the class's own hexagons, filtered.
//
// The module is pure (no Vue, no store, no map) for the same reason `multi.ts`
// and `derived.ts` are: the control, the pane, the key and the tests all read
// one definition of what a class IS, so a class cannot mean one thing in the
// control and another on the map.
import { derivedByID, type DerivedView } from "@/lib/h3/derived";
import {
	MULTI_MAP,
	MULTI_PARAM_FROM,
	MULTI_PARAM_RESOLUTION,
	MULTI_PARAM_TO,
	MULTI_SOURCE_LAYER,
	type MultiSeriesID,
} from "@/lib/h3/multi";
import {
	type SeriesMeasureID,
	seriesMeasureByID,
	seriesSlotField,
} from "@/lib/h3/series";

/** How the class is drawn: each qualifying cell, or the dissolved clusters. */
export type OutlineMode = "cell" | "cluster";

/** Which H3 layer the outline's variable comes from. All three share one
 * tessellation, so a class means the same cells whichever it reads. */
export type OutlineSourceID = "multi" | "coverage" | "status";

/** The comparison a class applies to its field.
 *
 * `id` is the STYLE-SPEC spelling the layer's filter is built from — MapLibre's
 * expression language has no `=` (equality is `==`), which is why the id is not
 * the printed operator. `label` is what the control and the key show, and `sql`
 * is the comparison the tile server's own CASE applies at this arm; the index is
 * what travels to the server as the `op` param. One list, so the client's filter,
 * the printed class and the server's arms cannot drift — the toml is read in the
 * tests to pin the last of those. */
export type OutlineOp = "==" | ">" | ">=" | "<" | "<=";

export interface OutlineOpDef {
	id: OutlineOp;
	label: string;
	sql: string;
	index: number;
}

export const OUTLINE_OPS: OutlineOpDef[] = [
	{ id: "==", label: "=", sql: "=", index: 0 },
	{ id: ">", label: ">", sql: ">", index: 1 },
	{ id: ">=", label: "≥", sql: ">=", index: 2 },
	{ id: "<", label: "<", sql: "<", index: 3 },
	{ id: "<=", label: "≤", sql: "<=", index: 4 },
];

export function outlineOpIndex(op: OutlineOp): number {
	return OUTLINE_OPS.find((entry) => entry.id === op)?.index ?? 0;
}

export function isOutlineOp(value: string): value is OutlineOp {
	return OUTLINE_OPS.some((entry) => entry.id === value);
}

/** One per-cell field of a source. `index` is the ordinal the tile server's
 * `field` param selects on — it is the field's position in that layer's CASE,
 * which is why it is stated here rather than derived from array order. `slot` is
 * present for the SERIES SLOTS: their per-cell value is the operator's series
 * under the active measure, so the MVT property their individual-cell filter
 * reads is resolved from the measure rather than spelled here. */
export interface OutlineField {
	id: string;
	label: string;
	index: number;
	slot?: number;
}

/** The tile parameters a source's grid map takes. These are the GRID's own
 * param names, so the individual-cell mode reads exactly the tile the fill
 * reads and reusing it is free. */
export interface OutlineGridParams {
	resolution: string;
	from: string;
	to: string;
	/** The grid's own extra knob: coverage's active-only, status's horizon. */
	extra: string | null;
}

/** The class parameters, on the dissolve map. Three names, shared across the
 * three dissolve maps: a class means the same thing whichever grid it reads. */
export interface OutlineClassParams {
	field: string;
	op: string;
	threshold: string;
}

export interface OutlineSource {
	id: OutlineSourceID;
	label: string;
	/** One line naming the tile the variable lives in, for the control. */
	tile: string;
	/** The ordinary grid map the individual-cell mode draws over. */
	grid: { map: string; layer: string } & OutlineGridParams;
	/** The dissolve map: the class is a param, the contour is the layer. */
	cluster: { map: string; layer: string } & OutlineGridParams &
		OutlineClassParams;
	/** The per-cell fields this source can outline. */
	fields: OutlineField[];
}

/** The two derived grids are read from their own definitions in `derived.ts`
 * rather than re-spelled here: the outline's individual-cell mode must draw the
 * very tile the fill draws, and a second spelling of its map name is exactly the
 * drift this module exists to prevent. */
function derivedGrid(
	view: DerivedView,
): { map: string; layer: string } & OutlineGridParams {
	return {
		map: view.map,
		layer: view.sourceLayer,
		resolution: view.params.resolution,
		from: view.params.from,
		to: view.params.to,
		extra: view.params.extra,
	};
}

const COVERAGE = derivedByID("coverage");
const STATUS = derivedByID("status");

export const OUTLINE_SOURCES: OutlineSource[] = [
	{
		id: "multi",
		label: "Combined H3 — the four district datasets",
		tile: MULTI_MAP,
		grid: {
			map: MULTI_MAP,
			layer: MULTI_SOURCE_LAYER,
			resolution: MULTI_PARAM_RESOLUTION,
			from: MULTI_PARAM_FROM,
			to: MULTI_PARAM_TO,
			extra: null,
		},
		cluster: {
			map: "h3-outline-multi",
			layer: "service-area-h3-outline-multi",
			resolution: "h3_outline_multi_resolution",
			from: "h3_outline_multi_from",
			to: "h3_outline_multi_to",
			extra: null,
			field: "h3_outline_multi_field",
			op: "h3_outline_multi_op",
			threshold: "h3_outline_multi_threshold",
		},
		fields: [
			// The four TABLES, then the four SERIES SLOTS. Every one of them is
			// the MEASURE's value rather than a bare count (the layer's CASE reads
			// `v_*`), which is what lets a class be drawn in acreage over a fill
			// drawn in acreage — and why none of their ids is an MVT property
			// name: `outlineCellFilter` resolves the property a class really reads
			// from the slot and the measure, because the same cell carries it
			// under `n_treatment` on the multi grid and under `n_s0`/`m_s0` on the
			// series grid. `slot` is that slot; the tables occupy 0..3, the roles
			// reuse the same four columns.
			{ id: "v_treatment", label: "Treatments in the cell", index: 0, slot: 0 },
			{
				id: "v_observation",
				label: "Inspections in the cell",
				index: 1,
				slot: 1,
			},
			{ id: "v_feature", label: "Features in the cell", index: 2, slot: 2 },
			{ id: "v_site", label: "Sites in the cell", index: 3, slot: 3 },
			{ id: "v_s0", label: "Fill A series in the cell", index: 4, slot: 0 },
			{ id: "v_s1", label: "Fill B series in the cell", index: 5, slot: 1 },
			{
				id: "v_s2",
				label: "Outline C series in the cell",
				index: 6,
				slot: 2,
			},
			{ id: "v_s3", label: "Total series in the cell", index: 7, slot: 3 },
		],
	},
	{
		id: "coverage",
		label: "Coverage grid — treated share",
		tile: COVERAGE.map,
		grid: derivedGrid(COVERAGE),
		cluster: {
			map: "h3-outline-coverage",
			layer: "service-area-h3-outline-coverage",
			resolution: "h3_outline_coverage_resolution",
			from: "h3_outline_coverage_from",
			to: "h3_outline_coverage_to",
			extra: "h3_outline_coverage_active_only",
			field: "h3_outline_coverage_field",
			op: "h3_outline_coverage_op",
			threshold: "h3_outline_coverage_threshold",
		},
		fields: [
			{ id: "n_treated", label: "Places treated in the window", index: 1 },
			{ id: "n_places", label: "Places in the cell", index: 0 },
			{ id: "n_active", label: "Active places", index: 2 },
		],
	},
	{
		id: "status",
		label: "Status grid — actionable now",
		tile: STATUS.map,
		grid: derivedGrid(STATUS),
		cluster: {
			map: "h3-outline-status",
			layer: "service-area-h3-outline-status",
			resolution: "h3_outline_status_resolution",
			from: "h3_outline_status_from",
			to: "h3_outline_status_to",
			extra: "h3_outline_status_horizon_days",
			field: "h3_outline_status_field",
			op: "h3_outline_status_op",
			threshold: "h3_outline_status_threshold",
		},
		fields: [
			{ id: "n_production", label: "Production found, untreated", index: 2 },
			{ id: "n_needs", label: "Actionable now", index: 1 },
			{ id: "n_places", label: "Places in the cell", index: 0 },
			{ id: "n_due", label: "Due for revisit", index: 3 },
		],
	},
];

export function outlineSourceByID(id: OutlineSourceID): OutlineSource {
	return (
		OUTLINE_SOURCES.find((source) => source.id === id) ?? OUTLINE_SOURCES[0]
	);
}

export function outlineFieldOf(
	source: OutlineSource,
	fieldID: string,
): OutlineField {
	return (
		source.fields.find((field) => field.id === fieldID) ?? source.fields[0]
	);
}

/** The H3 grid a mode fills with, in this module's own vocabulary — the source
 * whose tile the outline can read without an extra request. Anything else
 * (the density grid, the bubbles, the dots) has no H3 cell field at all, which
 * is why the outline is offered only on the trellised modes. */
export function fillOutlineSource(fillMode: string): OutlineSourceID | null {
	if (fillMode === "hex-coverage") return "coverage";
	if (fillMode === "hex-status") return "status";
	if (
		fillMode === "hex-bivariate" ||
		fillMode === "hex-diff" ||
		fillMode === "hex-breakdown"
	)
		return "multi";
	return null;
}

// ── Classes ────────────────────────────────────────────────────────────────

/** One class: a predicate on one field of one source, and the ink it is drawn
 * in. Several may be on at once — the outline carries a small categorical, not
 * just a yes/no — and each is its own request, because each is its own
 * predicate. */
export interface OutlineClass {
	/** Stable key for the control's rows and the layer ids. */
	id: string;
	source: OutlineSourceID;
	field: string;
	op: OutlineOp;
	threshold: number;
	color: string;
	width: number;
}

/** The hues a class cycles through. Deliberately loud: the outline's whole job
 * is to read over a fill it must not be mistaken for. */
export const OUTLINE_HUES = [
	"#dc2626",
	"#c2185b",
	"#ea580c",
	"#7c3aed",
	"#0891b2",
	"#4d7c0f",
];

/** The ink a new class takes, so two classes added in a row cannot be the same
 * colour. */
export function outlineHueFor(index: number): string {
	return OUTLINE_HUES[index % OUTLINE_HUES.length];
}

/** The class the control opens with: "never treated", the sparse exception the
 * outline exists to show, over the coverage fill. It is OFF until the operator
 * turns the outline on — a lab whose third variable drew itself would not be
 * comparing anything. */
export function defaultOutlineClass(): OutlineClass {
	return {
		id: "outline-1",
		source: "coverage",
		field: "n_treated",
		op: "==",
		threshold: 0,
		color: OUTLINE_HUES[0],
		width: 2.5,
	};
}

/** The class as an expression: what it selects, in the field's own spelling. A
 * field the tile carries under its own name prints that name (`n_treated = 0`
 * is the layer's own column); a SERIES SLOT has no name of its own — its value
 * is whichever series the role holds, in whichever measure — so it prints the
 * role's label instead of an internal column name. */
export function outlineClassText(cls: OutlineClass): string {
	const source = outlineSourceByID(cls.source);
	const field = outlineFieldOf(source, cls.field);
	const op = OUTLINE_OPS.find((entry) => entry.id === cls.op)?.label ?? cls.op;
	const subject = field.slot === undefined ? field.id : field.label;
	return `${subject} ${op} ${cls.threshold}`;
}

// ── Tile URLs ──────────────────────────────────────────────────────────────

export interface OutlineTileInput {
	cls: OutlineClass;
	base: string;
	resolution: number;
	from: string;
	to: string;
	/** Coverage only: the fill's own denominator knob, mirrored so an outline
	 * over the coverage grid reads the universe the fill counted. */
	activeOnly?: boolean;
	/** The series and the measure the fill is drawn with, mirrored onto the
	 * multi dissolve so a class over a SERIES SLOT reads the same variable the
	 * fill paints — the measure applies uniformly to every series and to the
	 * outline, and a class drawn in counts over a fill drawn in acres would be
	 * two questions in one picture. Only the multi source reads it: the derived
	 * grids carry one variable of their own and no series. */
	series?: OutlineSeriesContext;
}

/** The measure and the four slot assignments, in role order. */
export interface OutlineSeriesContext {
	measure: SeriesMeasureID;
	quantityUnit: string;
	areaUnit: string;
	slots: (number | null)[];
	/** Per-slot metric overrides, index = slot, from a saved layer's own
	 * variables. A null entry leaves the slot on the global `measure` above, so
	 * a caller that states none builds exactly today's URL. */
	measures?: (SeriesMeasureID | null)[];
	/** Per-slot quantity units, index = slot, read by the slot whose `measures`
	 * entry is quantity. Empty or null leaves the slot on the global `unit`. */
	units?: (string | null)[];
}

/** The multi dissolve map's series params. They mirror `h3-series`'
 * own, because the dissolve re-reads the base tables itself (see the layer's
 * note on the grown bbox) rather than joining the grid's tile. */
const OUTLINE_MULTI_SERIES_PARAMS = {
	measure: "h3_outline_multi_measure",
	unit: "h3_outline_multi_unit",
	areaUnit: "h3_outline_multi_area_unit",
	slot: [
		"h3_outline_multi_s0",
		"h3_outline_multi_s1",
		"h3_outline_multi_s2",
		"h3_outline_multi_s3",
	],
};

/** A slot's OWN metric and quantity unit on the multi dissolve — the saved
 * layers' extension of the one global `measure`/`unit` above. Both default to
 * the globals server-side, so a class that states neither is unchanged. */
export const OUTLINE_MULTI_PARAM_MEASURE_SLOT = (slot: number): string =>
	`h3_outline_multi_m${slot}`;
export const OUTLINE_MULTI_PARAM_UNIT_SLOT = (slot: number): string =>
	`h3_outline_multi_u${slot}`;

/** The window binds and organisation, under a param-name set. An absent bound
 * means unbounded to the server, exactly as on the grids. */
function windowParams(
	search: URLSearchParams,
	params: OutlineGridParams,
	input: OutlineTileInput,
): void {
	search.set(params.resolution, String(input.resolution));
	if (input.from) search.set(params.from, input.from);
	if (input.to) search.set(params.to, input.to);
	// Coverage's narrowing is the one knob the lab can set, and only its
	// non-default state travels — the same rule the coverage grid's own builder
	// follows, so the outline cannot read a different universe than the fill.
	if (params.extra && input.activeOnly && params.extra.includes("active_only"))
		search.set(params.extra, "1");
}

/**
 * The DISSOLVE tile for one class: the boundary of the union of the class's
 * cells, computed server-side. One class is one request — the class is a param,
 * so a class the operator edits is a new URL rather than a new layer family.
 *
 * It takes no fill-mode parameter on purpose: the outline is independent of the
 * encoding underneath it, so the same class over the bivariate and over the
 * total is the same string and MapLibre's source cache is reused when the fill
 * switches.
 */
export function outlineClusterTiles(input: OutlineTileInput): string[] {
	if (!input.base) return [];
	const source = outlineSourceByID(input.cls.source);
	const field = outlineFieldOf(source, input.cls.field);
	const search = new URLSearchParams();
	windowParams(search, source.cluster, input);
	search.set(source.cluster.field, String(field.index));
	search.set(source.cluster.op, String(outlineOpIndex(input.cls.op)));
	search.set(source.cluster.threshold, String(input.cls.threshold));
	if (input.series && source.id === "multi") {
		const context = input.series;
		// A caller that states per-slot metrics (a saved layer, whose every
		// variable carries its own) states every slot it assigned — the
		// dissolve's arm for a slot reads that SLOT's metric, and the lab's
		// shorthand of omitting a slot that already holds its default ordinal
		// would leave the arm on the global measure. The lab states neither
		// array, so its slots are written exactly as before.
		const perSlot =
			context.measures !== undefined || context.units !== undefined;
		if (context.measure !== "records")
			search.set(
				OUTLINE_MULTI_SERIES_PARAMS.measure,
				String(seriesMeasureByID(context.measure).index),
			);
		if (context.measure === "quantity" && context.quantityUnit)
			search.set(OUTLINE_MULTI_SERIES_PARAMS.unit, context.quantityUnit);
		// The area unit is the district's, so it stays one param however many
		// slots measure acreage — and a per-slot area metric no longer sets the
		// global measure, so the unit has to travel on its own account too.
		if (context.measure === "area" || context.measures?.includes("area"))
			search.set(OUTLINE_MULTI_SERIES_PARAMS.areaUnit, context.areaUnit);
		context.slots.forEach((index, slot) => {
			if (index === null) return;
			if (index === slot && !perSlot) return;
			search.set(OUTLINE_MULTI_SERIES_PARAMS.slot[slot], String(index));
		});
		context.measures?.forEach((measure, slot) => {
			if (!measure) return;
			search.set(
				OUTLINE_MULTI_PARAM_MEASURE_SLOT(slot),
				String(seriesMeasureByID(measure).index),
			);
		});
		context.units?.forEach((unit, slot) => {
			if (!unit) return;
			search.set(OUTLINE_MULTI_PARAM_UNIT_SLOT(slot), unit);
		});
	}
	return [`${input.base}maps/${source.cluster.map}/{z}/{x}/{y}?${search}`];
}

/**
 * The ordinary grid tile for a class's source: what INDIVIDUAL-CELL mode draws
 * the class's hexagons from. When the source is the fill's own tile the caller
 * reuses the fill's source and issues no request at all — the same template,
 * the same bytes, one layer more.
 */
export function outlineCellTiles(input: OutlineTileInput): string[] {
	if (!input.base) return [];
	const source = outlineSourceByID(input.cls.source);
	const search = new URLSearchParams();
	windowParams(search, source.grid, input);
	return [`${input.base}maps/${source.grid.map}/{z}/{x}/{y}?${search}`];
}

/** The multi grid's own per-cell columns, by slot — what a class reads when the
 * fill under it is the four-dataset grid rather than the series grid. */
const MULTI_TABLE_COLUMNS = [
	"n_treatment",
	"n_observation",
	"n_feature",
	"n_site",
];

/** What an individual-cell class reads a multi-grid field off: the property the
 * tile under the class actually carries. On the multi grid that is the table's
 * own column; on the series grid it is the column the MEASURE named (`n_s{slot}`
 * for records, `m_s{slot}` for a sum), because the series grid keeps the count
 * and the measure's sum side by side under exactly those names. */
export interface OutlineSeriesFilter {
	measure: SeriesMeasureID;
	seriesGrid: boolean;
}

/** The individual-cell class as a MapLibre filter over the source's own tile:
 * the class's cells, and nothing else. It is the trivial case of the dissolve —
 * the rings a cluster union would cancel stay.
 *
 * A class over a SERIES SLOT (or over one of the four tables, which occupy the
 * same slots) reads the property the MEASURE names, so the cells an
 * individual-cell class outlines are exactly the cells the dissolve map would
 * contour. */
export function outlineCellFilter(
	cls: OutlineClass,
	ctx: OutlineSeriesFilter,
): unknown[] {
	const source = outlineSourceByID(cls.source);
	const field = outlineFieldOf(source, cls.field);
	const property =
		source.id === "multi" && field.slot !== undefined
			? ctx.seriesGrid
				? seriesSlotField(field.slot, ctx.measure)
				: MULTI_TABLE_COLUMNS[field.slot]
			: field.id;
	return [cls.op, ["get", property], cls.threshold];
}

/** The ink: the class's own colour and width, opaque enough to read over a
 * saturated fill and thin enough not to hide it. */
export function outlineLinePaint(cls: OutlineClass): Record<string, unknown> {
	return {
		"line-color": cls.color,
		"line-width": cls.width,
		"line-opacity": 0.95,
	};
}

// ── The join ───────────────────────────────────────────────────────────────

export interface CellJoin {
	shared: number;
	/** Cells only the first tile has, and only the second. */
	onlyA: number;
	onlyB: number;
}

/**
 * The `cell` join between two H3 tiles of one z/x/y: the cells both carry.
 *
 * This is the whole of "joined by `cell` id". The two layers share the
 * tessellation and the tiling scheme, so the same cell appears in both tiles at
 * the same position, and a class read off one tile lands on the other's cells by
 * an id match — no geometry, no reprojection. A tile pair that did NOT share the
 * scheme would show up here as disjoint sets, which is exactly what the lab's
 * report counts on a sampled tile.
 */
export function sharedCells(a: string[], b: string[]): CellJoin {
	const setA = new Set(a);
	const setB = new Set(b);
	let shared = 0;
	for (const cell of setA) if (setB.has(cell)) shared++;
	return {
		shared,
		onlyA: setA.size - shared,
		onlyB: setB.size - shared,
	};
}

// ── Presets ────────────────────────────────────────────────────────────────

export type OutlinePresetID = "coverage-gaps" | "finding-it" | "actionable-now";

/** The fill encodings the presets pair an outline with. Declared here rather
 * than imported from `state.ts` so this module stays free of the store (the
 * values are the lab's own mode ids, and `state.ts` checks them). */
export type OutlineFillMode =
	| "hex-coverage"
	| "hex-bivariate"
	| "hex-breakdown";

export const PRESET_CLASS_ID = "preset";

/** The id of the class the Series panel's Outline C role writes: one class, at
 * the slot the role owns, so the third variable has exactly one rule to edit and
 * the panel can point at it rather than keeping a second copy of it. */
export const SERIES_CLASS_ID = "series-c";

export interface OutlinePreset {
	id: OutlinePresetID;
	label: string;
	/** The dashboard question this combination is the picture of. */
	question: string;
	/** One line saying what the two layers are. */
	blurb: string;
	/** The fill to turn on before the outline. `series`/`pair` pin the
	 * combination's datasets where the datum the class reads is one of them. */
	fill: {
		mode: OutlineFillMode;
		series?: MultiSeriesID[];
		pair?: [MultiSeriesID, MultiSeriesID];
	};
	/** The class the preset outlines. One per preset: a preset is a question,
	 * and a question has one exception to draw. */
	cls: Omit<OutlineClass, "id">;
}

export const OUTLINE_PRESETS: OutlinePreset[] = [
	{
		id: "coverage-gaps",
		label: "Coverage gaps",
		question: "Where have we not been?",
		blurb:
			"the coverage ramp with the places that got no treatment in the window outlined as clusters",
		fill: { mode: "hex-coverage" },
		cls: {
			source: "coverage",
			field: "n_treated",
			op: "==",
			threshold: 0,
			color: OUTLINE_HUES[0],
			width: 2.5,
		},
	},
	{
		id: "finding-it",
		label: "Where we're finding it",
		question: "Where did the latest inspection find production?",
		blurb:
			"treatments × inspections with the cells whose latest inspection found production — and that have not been treated since — outlined as clusters",
		fill: { mode: "hex-bivariate", pair: ["treatment", "observation"] },
		cls: {
			source: "status",
			field: "n_production",
			op: ">",
			threshold: 0,
			color: OUTLINE_HUES[1],
			width: 2.5,
		},
	},
	{
		id: "actionable-now",
		label: "Actionable now",
		question: "Where is the work waiting?",
		blurb:
			"the window's total activity with the cells holding an actionable place outlined as clusters — a sparse exception over a dense field",
		fill: { mode: "hex-breakdown" },
		cls: {
			source: "status",
			field: "n_needs",
			op: ">",
			threshold: 0,
			color: OUTLINE_HUES[2],
			width: 2.5,
		},
	},
];

export function outlinePresetByID(id: OutlinePresetID): OutlinePreset {
	return (
		OUTLINE_PRESETS.find((preset) => preset.id === id) ?? OUTLINE_PRESETS[0]
	);
}

// ── The key ────────────────────────────────────────────────────────────────

export interface OutlineLegendRow {
	color: string;
	width: number;
	label: string;
}

export interface OutlineLegend {
	title: string;
	/** What the outline is doing, in one line — the key must not describe a
	 * cluster as a cell. */
	note: string;
	rows: OutlineLegendRow[];
}

/** The outline's rows for the key. A drawn class the key omitted would be a mark
 * the operator cannot name, so the key is built from the same class list the
 * pane's layers are. `unit` is the measure's unit word while a MEASURE is being
 * drawn — a class threshold of "20" means nothing without it — and is left out
 * for the count measure, whose unit word ("records") adds nothing to a count. */
export function outlineLegend(
	classes: OutlineClass[],
	mode: OutlineMode,
	unit = "",
): OutlineLegend {
	const how =
		mode === "cluster" ? "dissolved clusters" : "each qualifying cell";
	const inUnit = unit && unit !== "records" ? ` (${unit})` : "";
	return {
		title: "Outline — the third variable",
		note:
			mode === "cluster"
				? `One contour per contiguous blob: the class's hexagons are unioned and only the union's boundary is drawn, so neighbouring cells of the class read as one shape.${inUnit ? ` The class is read in ${unit}.` : ""}`
				: `Every qualifying cell is outlined, so the cells of a blob stay countable.${inUnit ? ` The class is read in ${unit}.` : ""}`,
		rows: classes.map((cls) => {
			const source = outlineSourceByID(cls.source);
			return {
				color: cls.color,
				width: cls.width,
				label: `${outlineClassText(cls)}${inUnit} · ${how} · ${source.tile}`,
			};
		}),
	};
}
